import { pageTitle } from '@palaccount/config';
import type { GrantablePermission, RoleResponse } from '@palaccount/contracts';
import { ALL_GRANTABLE_PERMISSIONS, opensAnyScreen } from '@palaccount/contracts';
import { Copy, Lock, Plus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { useCan } from '@/lib/permissions';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { useMe } from '@/lib/session';

import { AdminTabs } from './admin-tabs';
import { useRoleMutations, useSecurityRoles } from './api';
import { RoleGrid, sameGrid, toggleGrid } from './role-grid';

/**
 * ADM-06 — Roles & Permissions (PART 15.1 / 16.4.4): the role rail (shipped
 * three first, locked with a padlock + Duplicate — BR-ADM-06-19/18; custom
 * roles after, alphabetically), the 12×5 editor grid, New custom role (name +
 * description + start-from), rename/re-describe, deactivate/reactivate.
 *
 * The rules the page mirrors (the API enforces them all):
 *  - a System grid is drawn read-only at FULL contrast (BR-ADM-06-04) and
 *    every edit path on it is replaced by Duplicate (ERR-ADM-06-01);
 *  - a switch the signed-in person does not hold themselves is greyed with
 *    the reason (BR-PERM-64/BR-ADM-06-17) — visually distinct from locked
 *    (BR-ADM-06-20);
 *  - switching roles with unsaved changes WARNS instead of discarding
 *    (BR-ADM-06-03);
 *  - deactivation of a held role is refused by the API naming the count
 *    (BR-ADM-06-14) — the message is surfaced verbatim;
 *  - there is no Delete anywhere (BR-ADM-06-15), and no Owner card
 *    (BR-ADM-06-07).
 *
 * DEVIATION, AUTHORISED: this grid saves behind a Save button rather than
 * applying each switch at once. That is the ADM-01 Save-button deviation
 * wearing this screen's clothes — BR-ADM-01-13 ("Changing a setting must apply
 * at once. There is no Save button") is the rule both diverge from, and the
 * PART 21 sweep (27 Aug 2026) found the pair argued in code and ratified
 * nowhere. Both are now covered by the "Ratified in passing (27 Aug)" block of
 * `DELEGATED RULINGS — 26 Aug 2026.md`, which authorises the deviation while
 * saying in as many words that BR-ADM-01-13 is UNRETIRED. The full note,
 * including the reversal path and why no retirement notice is claimed, lives
 * once at `apps/web/src/features/admin/settings-page.tsx` rather than being
 * restated here in words that could drift from it.
 *
 * On this screen the deviation is the milder half: a permission grid is a set
 * of switches that mean something only together, and BR-ADM-06-03 already
 * requires an unsaved-changes warning — a rule that presupposes unsaved
 * changes exist.
 */
export function RolesPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: roles, isPending, error } = useSecurityRoles();
  const { create, update, duplicate, setActive } = useRoleMutations();
  const canEdit = useCan('admin.edit');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [workingGrid, setWorkingGrid] = useState<GrantablePermission[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createFrom, setCreateFrom] = useState('');
  /** The role the user asked to switch to, held while ERR-ADM-06-04's box is
   *  open. `null` is closed — GEN-208's "nothing happens until the action
   *  button is clicked" is exactly this: the id does not move until one of the
   *  two acts runs. */
  const [pendingRole, setPendingRole] = useState<RoleResponse | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('roles.title'));
  }, [t]);

  const ordered = useMemo(() => {
    if (!roles) return [];
    // Shipped three first (API sorts), custom alphabetically after (§4).
    return roles;
  }, [roles]);

  // "When the screen opens, the Accountant role is already selected." (§4)
  const selected =
    ordered.find((role) => role.id === selectedId) ??
    ordered.find((role) => role.name === 'Accountant') ??
    ordered[0];

  useEffect(() => {
    if (!selected) return;
    setWorkingGrid([...selected.permissions]);
    setName(selected.name);
    setDescription(selected.description ?? '');
  }, [selected?.id, selected?.permissions.join(',')]);

  /** BR-PERM-64: the caller may only flick ON what they themselves hold. */
  const ungrantable = useMemo(() => {
    if (me?.user.isOwner) return new Set<GrantablePermission>();
    const held = new Set<string>(me?.permissions ?? []);
    return new Set(ALL_GRANTABLE_PERMISSIONS.filter((permission) => !held.has(permission)));
  }, [me]);

  const dirty =
    selected !== undefined &&
    !selected.system &&
    (!sameGrid(workingGrid, selected.permissions) ||
      name !== selected.name ||
      description !== (selected.description ?? ''));

  /**
   * BR-ADM-06-03 / ERR-ADM-06-04 — never silently discard an unsaved grid.
   *
   * This used to be a one-line call to the browser's own confirm(), and it was
   * wrong twice over.
   *
   * FIRST, browser chrome is neither of BR-PERM-15's two patterns (spec line
   * 35168: "a new pattern must not be invented"), and its OK button breaches
   * §8's "the verb, never the word OK" and GEN-206 outright.
   *
   * SECOND, AND WORSE: the sentence it showed was the SPEC'S SENTENCE
   * INVERTED. `roles.unsavedWarn` read "You changed permissions on {name}.
   * Discard them?" against a spec that says, identically at line 31927
   * (BR-ADM-06-03's `Message:`) and line 32061 (ERR-ADM-06-04), "You changed
   * permissions on {name}. SAVE them before switching?" — so OK meant DISCARD
   * where the affirmative word promised SAVE. A person who read the question,
   * agreed with it, and clicked the affirmative button lost exactly the work
   * they had just agreed to keep. A two-button box could not have been made
   * right by rewording alone: with three meaningful answers, one of them has
   * nowhere to go.
   *
   * ERR-ADM-06-04's remedy column names all three — "Save or discard", plus
   * the third that every dialog has:
   *
   *   Save     → save these edits, THEN switch. Not "switch and hope."
   *   Discard  → throw the edits away and switch. The only destroying path.
   *   Cancel   → GEN-207 (spec line 4706), "close the dialog and do nothing":
   *              stay on this role with every edit intact. Escape and the
   *              backdrop are the same event and mean the same thing.
   *
   * The card you are ALREADY on is a no-op rather than a prompt. Under the old
   * confirm() it raised the warning and then, on OK, re-selected the same id —
   * which changed nothing, because the reset effect keys on `selected?.id`. A
   * modal whose Discard genuinely resets the working copy would have turned
   * that harmless oddity into a way to destroy your edits by clicking the card
   * you were looking at. Refusing the prompt is the reading that cannot
   * destroy work.
   */
  const selectRole = (role: RoleResponse) => {
    if (role.id === selected?.id) return;
    if (dirty) {
      setPendingRole(role);
      return;
    }
    setPageError(null);
    setSelectedId(role.id);
  };

  /**
   * ERR-ADM-06-05 — "Lost the network while flicking a switch" → *"That change
   * was not saved. Check your connection and try again."* → *Try the switch
   * again* (spec line 32062).
   *
   * ── THE DISCRIMINATOR IS "DID THE SERVER ANSWER", NOT "IS THE NETWORK UP" ─
   *
   * `throwApiError` mints an `ApiError` only from a response that ARRIVED, so
   * an `ApiError` means the server considered the change and refused it —
   * a colliding name (ERR-ADM-06-02 — the server's sentence names the name:
   * "A role called Accountant already exists. Pick another name.", built in
   * RolesService.withUniqueName and surfaced here unchanged), ERR-ADM-06-03's
   * empty grid, BR-PERM-70's locked role — and every one of those has its own
   * sentence which must be surfaced unchanged. Anything else means the
   * request never completed at all: `fetch` rejected and ts-rest let the
   * rejection through untouched.
   *
   * That is a stronger claim than the rule's own wording and a weaker one than
   * "the network dropped", which is the point. This call site cannot see a
   * cable; it can see that nothing came back, and a proxy that never replied
   * is the same event to the person as a lost connection — their change is not
   * saved and trying again is the right advice. Before this, the reader met
   * the browser's `Failed to fetch` in the browser's language.
   *
   * ── WHAT THIS MUST NOT BECOME ──────────────────────────────────────────────
   *
   * ONE SHARED "the network failed" STRING FOR THE PRODUCT. `ERR-ADM-08-02`
   * (spec line 32962) is the same situation on the personalization screen with
   * a DIFFERENT sentence — "That change was not saved to your account. It
   * applies on this device until you sign in again." — because that screen's
   * change survives locally and the sentence says so. Consolidating the two
   * would make the product less true on one of them. The reusable part is this
   * DISCRIMINATOR, not the wording.
   *
   * The same `cause instanceof Error ? cause.message` shape this replaces is
   * still live across the rest of the web app. Fixing it there is a wave of
   * its own and is deliberately not done here.
   */
  const fail = (cause: unknown) => {
    if (!(cause instanceof ApiError)) {
      setPageError(t('roles.errSaveFailed'));
      return;
    }
    setPageError(localizeServerMessage(t, cause.message));
  };

  /** Returns whether the save actually landed — `switchAfterSaving` may not
   *  move the selection on a refusal, or the edits go with it. */
  const save = async () => {
    if (!selected) return false;
    setPageError(null);
    try {
      await update.mutateAsync({
        roleId: selected.id,
        body: {
          name: name.trim(),
          description: description.trim(),
          permissions: workingGrid,
        },
      });
      return true;
    } catch (cause) {
      fail(cause);
      return false;
    }
  };

  /**
   * ERR-ADM-06-04's affirmative act, and the ORDER IS THE RULE: save first,
   * switch only if the server took it. The API refuses a grid that opens no
   * screen (ERR-ADM-06-03) and a name that collides (ERR-ADM-06-02), so  rule-coverage:ignore
   * "switch anyway" would answer "Save them before switching?" with a refusal
   * the user never sees and a grid that is gone. On a refusal the dialog
   * closes and the page error shows — closing, because the error renders
   * BEHIND the modal and a message nobody can read is not a message. Nothing
   * is lost either way: the edits are still in the working copy and the
   * selection has not moved.
   */
  const switchAfterSaving = async () => {
    const role = pendingRole;
    if (!role) return;
    const saved = await save();
    setPendingRole(null);
    if (!saved) return;
    setSelectedId(role.id);
  };

  /** The destroying path, and the only one. Resets the working copy to what
   *  the server holds before moving, so the function's name is true on its own
   *  rather than by way of the reset effect happening to fire afterwards. */
  const switchDiscarding = () => {
    const role = pendingRole;
    if (!role) return;
    if (selected) {
      setWorkingGrid([...selected.permissions]);
      setName(selected.name);
      setDescription(selected.description ?? '');
    }
    setPendingRole(null);
    setPageError(null);
    setSelectedId(role.id);
  };

  const doDuplicate = async (role: RoleResponse) => {
    setPageError(null);
    try {
      const copy = await duplicate.mutateAsync(role.id);
      setSelectedId(copy.id); // selected, name ready to edit (BR-PERM-71)
    } catch (cause) {
      fail(cause);
    }
  };

  const submitCreate = async () => {
    setPageError(null);
    try {
      const source = ordered.find((role) => role.id === createFrom);
      const created = await create.mutateAsync({
        name: createName.trim(),
        ...(createDescription.trim() === '' ? {} : { description: createDescription.trim() }),
        permissions: source ? [...source.permissions] : [],
      });
      setCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      setCreateFrom('');
      setSelectedId(created.id);
    } catch (cause) {
      fail(cause);
    }
  };

  const toggleActive = async (role: RoleResponse) => {
    setPageError(null);
    try {
      await setActive.mutateAsync({ roleId: role.id, active: !role.active });
    } catch (cause) {
      fail(cause);
    }
  };

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {localizeServerMessage(t, error.message)}
      </p>
    );
  }

  return (
    <div className="grid gap-4">
      {/*
        ERR-ADM-06-04, in the product's ONE shared box (GEN-204: "a screen must
        not build its own"). GEN-205 asks for four things and this is where its
        two text slots go:

          title →  the spec's sentence, WHOLE and unsplit. It already ends in a
                   question, so it satisfies "a title that asks a question"
                   without being cut in half, and reading the open dialog top
                   to bottom gives line 32061 back verbatim — the same property
                   `DeleteConfirmBox` keeps for GEN-58's sentence.
          body  →  "what will happen and that it cannot be undone", which the
                   mandated sentence does not say on its own. It names DISCARD
                   as the destroying answer, because the title's question names
                   only the saving one.

        `busy` is the save mutation's own pending flag, so GEN-60/GEN-161's
        grey-plus-spinner covers the round trip that Save now waits for.
      */}
      <ConfirmBox
        open={pendingRole !== null}
        title={t('roles.unsavedWarn', { name: selected?.name ?? '' })}
        body={t('roles.unsavedBody')}
        confirmLabel={t('common.save')}
        onConfirm={() => void switchAfterSaving()}
        secondaryLabel={t('roles.unsavedDiscard')}
        onSecondary={switchDiscarding}
        onCancel={() => setPendingRole(null)}
        busy={update.isPending}
      />
      <AdminTabs />
      <div className="flex flex-wrap items-center gap-3">
        <p className="me-auto max-w-prose text-sm text-muted-foreground">{t('roles.intro')}</p>
        <Gated permission="admin.create" reasonKey="perm.cannot.createRoles">
          <Button
            onClick={() => {
              setCreateOpen(true);
              setPageError(null);
            }}
          >
            <Plus aria-hidden /> {t('roles.newRole')}
          </Button>
        </Gated>
      </div>

      {pageError ? (
        <p role="alert" className="text-sm text-destructive">
          {pageError}
        </p>
      ) : null}

      {createOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('roles.createTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="role-name">{t('roles.fName')}</Label>
                <Input
                  id="role-name"
                  maxLength={80}
                  value={createName}
                  onChange={(event) => setCreateName(event.target.value)}
                />
              </div>
              {/* BR-PERM-56 — the two starting points, both required and both
                  here: "An empty grid" is the blank option, and every role in
                  `ordered` — shipped AND custom — is offered as "Copy of …".
                  The list is deliberately unfiltered on `system`, because
                  copying a locked role is the only route to customizing one
                  (BR-PERM-70/71). The one-click Duplicate on each role card is
                  the same path reached from the other direction. */}
              <div className="grid gap-1.5">
                <Label htmlFor="role-from">{t('roles.fStartFrom')}</Label>
                <NativeSelect
                  id="role-from"
                  value={createFrom}
                  onChange={(event) => setCreateFrom(event.target.value)}
                >
                  <option value="">{t('roles.emptyGrid')}</option>
                  {ordered.map((role) => (
                    <option key={role.id} value={role.id}>
                      {t('roles.copyOf', { name: role.name })}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="role-desc">{t('roles.fDescription')}</Label>
              <Textarea
                id="role-desc"
                rows={2}
                maxLength={500}
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => void submitCreate()}
                disabled={create.isPending || createName.trim() === ''}
              >
                {t('common.create')}
              </Button>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* The role rail. There is no Owner card, by design (BR-ADM-06-07). */}
        <div className="grid content-start gap-2">
          {isPending ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> : null}
          {ordered.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => selectRole(role)}
              data-testid={`role-card-${role.name}`}
              className={`rounded-lg border p-3 text-start transition-shadow ${
                role.id === selected?.id
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'hover:bg-muted/50'
              } ${role.active ? '' : 'opacity-60'}`}
            >
              <span className="flex items-center gap-2">
                <span className="me-auto text-sm font-semibold">{role.name}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    role.system
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                  }`}
                >
                  {role.system ? t('roles.system') : t('roles.custom')}
                </span>
                {role.system ? (
                  <span title={t('roles.lockTip')}>
                    <Lock aria-hidden className="size-3.5 text-amber-700 dark:text-amber-400" />
                  </span>
                ) : null}
                {!role.active ? (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {t('roles.inactive')}
                  </span>
                ) : null}
              </span>
              {role.description ? (
                <span className="mt-1 block text-xs text-muted-foreground">{role.description}</span>
              ) : null}
              <span className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Users aria-hidden className="size-3.5" />
                {t('roles.holders', { count: role.holders })}
              </span>
            </button>
          ))}
        </div>

        {selected ? (
          <Card className="self-start">
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <span>{selected.name}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {t('roles.permissionSet')}
                </span>
                {selected.system ? (
                  <>
                    <Lock aria-hidden className="size-4 text-amber-700 dark:text-amber-400" />
                    <span className="text-sm font-normal text-muted-foreground">
                      {t('roles.shipsLocked')}
                    </span>
                  </>
                ) : null}
                <span className="ms-auto flex gap-2">
                  <Gated permission="admin.create" reasonKey="perm.cannot.createRoles">
                    <Button variant="outline" size="sm" onClick={() => void doDuplicate(selected)}>
                      <Copy aria-hidden /> {t('roles.duplicate')}
                    </Button>
                  </Gated>
                  {/* A SHIPPED role cannot be deactivated by anybody, Owner
                      included (R18 locks all three) — a status rule, not a
                      permission one, so it stays an absence. */}
                  {!selected.system ? (
                    <Gated permission="admin.edit" reasonKey="perm.cannot.editRoles">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void toggleActive(selected)}
                        disabled={setActive.isPending}
                      >
                        {selected.active ? t('roles.deactivate') : t('roles.reactivate')}
                      </Button>
                    </Gated>
                  ) : null}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              {!selected.system && canEdit ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="edit-name">{t('roles.fName')}</Label>
                    <Input
                      id="edit-name"
                      maxLength={80}
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="edit-desc">{t('roles.fDescription')}</Label>
                    <Input
                      id="edit-desc"
                      maxLength={500}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </div>
                </div>
              ) : null}

              <RoleGrid
                permissions={workingGrid}
                locked={selected.system || !canEdit}
                ungrantable={ungrantable}
                onToggle={(permission) => setWorkingGrid((grid) => toggleGrid(grid, permission))}
              />

              {selected.system ? (
                <p className="text-xs text-muted-foreground">{t('roles.lockTip')}</p>
              ) : dirty ? (
                <div className="flex flex-wrap items-center gap-2">
                  {/* ERR-ADM-06-03 — a grid with every View off cannot be
                      saved, and the greyed button says why (BR-PERM-17's
                      shape; the server refuses the same grid with the same
                      sentence). `opensAnyScreen` is the contracts helper the
                      server uses, so the grey and the refusal cannot drift. */}
                  <Button
                    onClick={() => void save()}
                    disabled={update.isPending || name.trim() === '' || !opensAnyScreen(workingGrid)}
                    title={!opensAnyScreen(workingGrid) ? t('roles.errNoViewLeft') : undefined}
                  >
                    {t('common.save')}
                  </Button>
                  {!opensAnyScreen(workingGrid) ? (
                    <p className="text-xs text-destructive" role="note">
                      {t('roles.errNoViewLeft')}
                    </p>
                  ) : null}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setWorkingGrid([...selected.permissions]);
                      setName(selected.name);
                      setDescription(selected.description ?? '');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
